import React from 'react'
import { Box, Grid, Paper, Skeleton, Stack, useMediaQuery, useTheme } from '@mui/material'
import { BarChartSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { makeStyles } from 'tss-react/mui'
import { CUSTOM_TABLET_BREAKPOINT } from '../models/dashboard.model'

const useStyles = makeStyles()(theme => ({
  cardStats: {
    marginTop: theme.spacing(2),
  },
  cardSkeleton: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  },
  filterSkeleton: {
    borderRadius: theme.spacing(theme.shape.borderRadius),
    height: 36,
    width: 100,
  },
  dateRangeSkeleton: {
    width: '100%',
    height: 35,
  },
  chipsSkeleton: {
    width: 80,
    height: 35,
  },
}))

/**
 * Skeleton dashboard component, it is visible on sessions loading.
 * No params required
 */
export const DashboardSkeleton = (): JSX.Element => {
  const { classes, cx } = useStyles()
  const theme = useTheme()
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down(CUSTOM_TABLET_BREAKPOINT))

  return (
    <Box mt={2} data-qa={'e-mobility-dashboard-skeleton'}>
      <Paper>
        {isMobileOrTablet ? (
          <Grid container alignItems="center">
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="space-between" columnGap={1} width="100%">
                <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.chipsSkeleton)} />
                <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.chipsSkeleton)} />
                <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.chipsSkeleton)} />
                <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.chipsSkeleton)} />
              </Stack>
            </Grid>

            <Grid item xs={8}>
              <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.dateRangeSkeleton)} />
            </Grid>

            <Grid item xs={4}>
              <Stack width="100%" direction="row" justifyContent="flex-end">
                <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.chipsSkeleton)} />
              </Stack>
            </Grid>
          </Grid>
        ) : (
          <Grid container alignItems="center">
            <Grid item xs={3} lg={4}>
              <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.chipsSkeleton)} />
            </Grid>

            <Grid item xs={5} lg={4}>
              <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.dateRangeSkeleton)} />
            </Grid>

            <Grid item xs={4} lg={4}>
              <Stack direction="row" justifyContent="flex-end" columnGap={1} width="100%">
                <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.chipsSkeleton)} />
                <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.chipsSkeleton)} />
                <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.chipsSkeleton)} />
                <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.chipsSkeleton)} />
              </Stack>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Stack direction="row" columnGap={1} className={classes.cardStats}>
        <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.filterSkeleton)} />
        <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.filterSkeleton)} />
        <Skeleton variant="rounded" className={cx(classes.cardSkeleton, classes.filterSkeleton)} />
      </Stack>

      <Paper sx={{ mt: 4 }}>
        <BarChartSkeleton height={isMobileOrTablet ? 250 : 350} />
      </Paper>

      <Stack direction="row" columnGap={1} rowGap={1} justifyContent="space-between" className={classes.cardStats} flexWrap="wrap">
        <Skeleton variant="rounded" height={85} width={isMobileOrTablet ? '49%' : '24%'} className={classes.cardSkeleton} />
        <Skeleton variant="rounded" height={85} width={isMobileOrTablet ? '49%' : '24%'} className={classes.cardSkeleton} />
        <Skeleton variant="rounded" height={85} width={isMobileOrTablet ? '49%' : '24%'} className={classes.cardSkeleton} />
        <Skeleton variant="rounded" height={85} width={isMobileOrTablet ? '49%' : '24%'} className={classes.cardSkeleton} />
      </Stack>
    </Box>
  )
}
